import type { MinorAmount } from "./BetSystemTypes";

/**
 * How an amount that is not itself a ladder step picks one.
 *
 * `nearest` — the smallest absolute delta. For an operator-configured starting bet, where landing
 * on the step the operator meant matters more than the direction of the rounding.
 *
 * `at_or_below` — the largest step that does not exceed the amount, falling back to the lowest step
 * when the ladder has nothing that low. For a bet carried in from a round the player already played
 * (a pending-round restore, a replay): rounding UP would hand them a next wager larger than the
 * round being restored, which is not a change the client may make on their behalf (user, 2026-09-21).
 */
export type BetStepRounding = "nearest" | "at_or_below";

export function resolveInitialBetStepIndex(input: {
  betStepsMinor: MinorAmount[];
  initialBetMinor: MinorAmount;
  /** Defaults to `nearest`. */
  rounding?: BetStepRounding;
}): number {
  const { betStepsMinor, initialBetMinor } = input;

  if (betStepsMinor.length === 0) return 0;

  // exact match first
  const exact = betStepsMinor.indexOf(initialBetMinor);
  if (exact >= 0) return exact;

  const target = BigInt(initialBetMinor);

  if (input.rounding === "at_or_below") return resolveIndexAtOrBelow(betStepsMinor, target);

  // otherwise choose closest by absolute delta
  let bestIndex = 0;
  let bestDelta: bigint | null = null;

  for (let i = 0; i < betStepsMinor.length; i++) {
    const step = BigInt(betStepsMinor[i]);
    const delta = step >= target ? (step - target) : (target - step);

    if (bestDelta === null || delta < bestDelta) {
      bestDelta = delta;
      bestIndex = i;
    }
  }

  return bestIndex;
}

/** The highest step not above `target`; the lowest step when the ladder has nothing that low. */
function resolveIndexAtOrBelow(betStepsMinor: MinorAmount[], target: bigint): number {
  let bestIndex: number | null = null;
  let bestStep = 0n;
  let lowestIndex = 0;
  let lowestStep: bigint | null = null;

  for (let i = 0; i < betStepsMinor.length; i++) {
    const step = BigInt(betStepsMinor[i]);

    if (lowestStep === null || step < lowestStep) {
      lowestStep = step;
      lowestIndex = i;
    }

    if (step > target) continue;
    if (bestIndex === null || step > bestStep) {
      bestStep = step;
      bestIndex = i;
    }
  }

  return bestIndex ?? lowestIndex;
}